import { Link, matchPath, useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { REALM_ITEMS } from '../../constants/realmNav';
import { WAR_ITEMS } from '../../constants/warNav';
import { WORLD_ITEMS } from '../../constants/worldNav';

/**
 * SF-122 Component 1: bottom tab bar for the mobile app shell — Home/Realm/War/Kingdom/World,
 * the Five Parts IA (see spec Overview). Real links, not `<button>`s, so tabs carry browser-native
 * semantics (Phase 2 C12).
 *
 * SF-128: the Realm tab now also renders on all four `REALM_ITEMS` routes (via `Layout.tsx`'s
 * sibling-row injection), not just `/realm` itself, so active-tab detection generalizes beyond
 * "does the pathname exactly equal this tab's `to`." SF-146 applies the identical `activeMatch`
 * treatment to the War tab (`/war` + all three `WAR_ITEMS` routes). `NavLink`'s default (undocumented but
 * load-bearing) behavior is prefix matching — replaced here with `matchPath`, react-router's own
 * matching primitive, so that prefix semantics are preserved exactly rather than re-implemented
 * and subtly gotten wrong. `aria-current` is split: `"page"` on the tab's own exact route,
 * `"location"` when active only via prefix/`activeMatch` — avoids two simultaneous
 * `aria-current="page"` elements on one screen (this tab bar's Realm tab + `SiblingRow`'s
 * exact-match pill).
 */
interface TabItem {
  label: string;
  to: string;
  iconPath: string;
  activeMatch?: readonly string[];
}

const TABS: readonly TabItem[] = [
  { label: 'Home',    to: '/province', iconPath: 'M4 12H12M5 4H11L12 2H4L5 4ZM6 4V8H10V4M8 8V12' },
  { label: 'Realm',   to: '/realm',    iconPath: 'M3 14V6L8 2L13 6V14M3 14H13M6 14V10H10V14', activeMatch: REALM_ITEMS.map((item) => item.route) },
  { label: 'War',     to: '/war',      iconPath: 'M2 3H14L13 8H3ZM4 8V13H12V8M8 3V8', activeMatch: WAR_ITEMS.map((item) => item.route) },
  { label: 'Kingdom', to: '/kingdom',  iconPath: 'M3 5H13M8 5V14M4 8H12M5 11H11' },
  // SF-171: activeMatch generated from WORLD_ITEMS (Target Finder joins the set), removing the
  // manual-sync hazard the old hardcoded 2-item array had.
  // SF-155: `to` repointed from `/intel` to `/world` now that the World Hub exists — `/world` is
  // not itself a WORLD_ITEMS member (exactly like `/war` and `/kingdom` aren't members of their
  // own item-sets), so this tab's own `isExact` check and SiblingRow's exact-match pill on
  // `/intel` never both fire at once (the double-`aria-current="page"` collision the old deferral
  // comment flagged).
  { label: 'World',   to: '/world',    iconPath: 'M8 2C4.5 2 2 5 2 8H14C14 5 11.5 2 8 2ZM4 10L3 14M12 10L13 14M6 10V14M10 10V14', activeMatch: WORLD_ITEMS.map((item) => item.route) },
];

export function MobileTabBar() {
  const { pathname } = useLocation();

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 inset-x-0 flex items-stretch z-40"
      style={{
        backgroundColor: '#0b0e19',
        borderTop: `1px solid ${COLORS.border}`,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {TABS.map((tab) => {
        const isExact = !!matchPath({ path: tab.to, end: true }, pathname);
        const isSectionActive =
          !!matchPath({ path: tab.to, end: false }, pathname) ||
          (tab.activeMatch ?? []).some((route) => !!matchPath({ path: route, end: false }, pathname));
        const ariaCurrent = isExact ? 'page' : isSectionActive ? 'location' : undefined;

        return (
          <Link
            key={tab.to}
            to={tab.to}
            aria-current={ariaCurrent}
            className={`flex-1 flex flex-col items-center justify-center gap-1 ${isSectionActive ? 'font-semibold' : ''}`}
            style={{
              minHeight: 44,
              minWidth: 44,
              padding: '6px 4px',
              color: isSectionActive ? COLORS.accentStrong : '#7b83a5',
              textDecoration: 'none',
            }}
          >
            <svg width={18} height={18} viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d={tab.iconPath} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: 10 }}>{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
